import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropzone from 'react-dropzone';
import moment from 'moment';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  IconButton,
  Checkbox,
  Radio,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Drawer,
  TextField,
} from '@material-ui/core';

import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import ProgressCircle from './components/ProgressCircle';
import FlowStakeHolders from './components/FlowStakeHolders';
import Container from '../atoms/Container/Container';
import { CardWrapper } from './style';
import drawerHoc from '../../../../components/drawerHOC/DrawerHOC';
import { tenantActions } from '../../redux/tenant.actions';
import { TenantService } from '../../redux/tenant.service';
import { showErrorToast, showSuccesToast } from '../helpers';

const assuredPeriodicImg = require('./components/assets/assured-periodic.png');
const nhaAgreementImg = require('./components/assets/nha-agreement.png');
const getImgSrc = (img) => (typeof img === 'object' && img?.default ? img.default : img?.uri || img);

const AlertBox = ({ severity = 'info', children, style }) => {
  let bg = '#EFF8FF';
  let border = '#B9E6FE';
  let color = '#1E40AF';
  let IconComp = InfoOutlinedIcon;
  let iconColor = '#0288D1';

  if (severity === 'error') {
    bg = '#FEF2F2';
    border = '#FECACA';
    color = '#991B1B';
    IconComp = ErrorOutlineIcon;
    iconColor = '#DC2626';
  } else if (severity === 'warning') {
    bg = '#FFFBEB';
    border = '#FEF08A';
    color = '#92400E';
    IconComp = WarningIcon;
    iconColor = '#D97706';
  } else if (severity === 'success') {
    bg = '#F0FDF4';
    border = '#BBF7D0';
    color = '#166534';
    IconComp = InfoOutlinedIcon;
    iconColor = '#16A34A';
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: bg,
        borderRadius: '8px',
        border: `1px solid ${border}`,
        color: color,
        fontSize: '14px',
        lineHeight: 1.5,
        ...style,
      }}
    >
      <IconComp style={{ color: iconColor, marginRight: '12px', fontSize: '20px', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

const SendEsignatureUrlDrawer = ({ open, toggle, onSendEmail, signatureEmail }) => {
  const subject = 'URL for agreement esignature';

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={toggle}
      PaperProps={{ style: { width: 400, maxWidth: '90vw' } }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px 24px',
          justifyContent: 'space-between',
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>Send Email</Typography>
        <IconButton size="small" onClick={toggle}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 20 }}>
          <TextField
            fullWidth
            type="email"
            label="To"
            variant="outlined"
            value={signatureEmail}
            disabled
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <TextField
            fullWidth
            label="Subject"
            variant="outlined"
            value={subject}
            disabled
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <TextField
            fullWidth
            rows={8}
            multiline
            label="Message"
            variant="outlined"
            value={`Hello,\n\nHere is the link for esignature for tenancy agreement\n\nhttps://www.yotisign.com/sign/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\n\nThanks`}
            disabled
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            size="medium"
            variant="contained"
            color="primary"
            onClick={() => onSendEmail({ to: signatureEmail })}
          >
            Send
          </Button>
          <Button
            size="medium"
            variant="outlined"
            color="default"
            onClick={toggle}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

const radioData = [
  {
    title: 'Assured Periodic Tenancy',
    value: 'ast',
    content: 'Assured Periodic Tenancy Agreement is automatically created using the information you have already populated.',
  },
  {
    title: 'Non-Housing Act Agreement',
    value: 'nha',
    content: 'An Non-Housing Act Agreement is automatically created using the information you have already populated.',
  },
  {
    title: 'Bespoke',
    value: 'bespoke',
    content: 'You can upload the bespoke signed contract and send it to you tenant for thier record.',
  },
];

function hasAnyGuarantor(data) {
  if (!Array.isArray(data) || data.length === 0) return false;
  return data.some(
    (t) =>
      t?.open_flow_guarantor?.open_flow_guarantor_id &&
      t?.open_flow_guarantor?.email &&
      t.open_flow_guarantor.email.trim() !== ''
  );
}

const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : '');

const TenancyInfoAndAgreement = (props) => {
  const dispatch = useDispatch();
  const flowId = props?.route?.params?.id;
  const tenancyInfo = useSelector((state) => state.tenant?.flowIdInfo);

  const [selected, setSelected] = useState('ast');
  const [expanded, setExpanded] = useState('');
  const [guarantorChecked, setGuarantorChecked] = useState(false);
  const [docs, setDocs] = useState([]);
  const [parsedBespokeContracts, setParsedBespokeContracts] = useState([]);
  const [selectedBespokeContract, setSelectedBespokeContract] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showEsignatureModal, setShowEsignatureModal] = useState(false);
  const [showAccountInfo, setShowAccountInfo] = useState(false);

  const [openDocsModal, setOpenDocsModal] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);

  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectedLandlords, setSelectedLandlords] = useState([]);
  const [selectedGuarantors, setSelectedGuarantors] = useState([]);

  const [sendUrlOpen, setSendUrlOpen] = useState(false);
  const [signatureEmail, setSignatureEmail] = useState('');
  const [signatureToken, setSignatureToken] = useState('');
  const [signatureRole, setSignatureRole] = useState('');

  useEffect(() => {
    if (flowId) {
      dispatch(tenantActions.getFlowInfo(flowId));
      dispatch(tenantActions.getSignedObj(flowId, () => {}));
    }
  }, [flowId]);

  useEffect(() => {
    if (tenancyInfo) {
      if (Array.isArray(tenancyInfo.additional_contract_documents)) {
        setDocs(tenancyInfo.additional_contract_documents);
      } else {
        setDocs([]);
      }

      if (tenancyInfo.is_ast_generated) {
        setSelected('ast');
      } else if (tenancyInfo.is_nha_generated) {
        setSelected('nha');
      } else if (tenancyInfo.bespoke_contract_url) {
        try {
          setParsedBespokeContracts(JSON.parse(tenancyInfo.bespoke_contract_url));
        } catch (e) {
          setParsedBespokeContracts([]);
        }
        setSelected('bespoke');
      } else if (tenancyInfo?.draft_agreement_type?.toUpperCase() === 'AST') {
        setSelected('ast');
      } else if (tenancyInfo?.draft_agreement_type?.toUpperCase() === 'NHA') {
        setSelected('nha');
      } else {
        setSelected('ast');
        setParsedBespokeContracts([]);
      }
    }
  }, [tenancyInfo]);

  const onRefresh = () => {
    if (flowId) {
      dispatch(tenantActions.getFlowInfo(flowId));
    }
  };

  const handleMarkAsComplete = (e) => {
    const isChecked = e.target.checked;
    const endpoint = isChecked
      ? `/openflow/landlord_status/mark-as-complete?flow_id=${flowId}`
      : `/openflow/landlord_status/mark-as-incomplete?flow_id=${flowId}`;
    dispatch(tenantActions.toggleComplete(endpoint, flowId));
  };

  const handleChange1 = (contractType) => {
    setGuarantorChecked(false);
    if (contractType === 'ast') {
      if (tenancyInfo?.is_nha_generated) {
        showErrorToast('Please delete NHA contract first');
        return;
      } else if (tenancyInfo?.bespoke_contract_url) {
        showErrorToast('Please delete Bespoke contract first');
        return;
      }
    }
    if (contractType === 'nha') {
      if (tenancyInfo?.is_ast_generated) {
        showErrorToast('Please delete APT contract first');
        return;
      } else if (tenancyInfo?.bespoke_contract_url) {
        showErrorToast('Please delete Bespoke contract first');
        return;
      }
    }
    if (contractType === 'bespoke') {
      if (tenancyInfo?.is_ast_generated) {
        showErrorToast('Please delete APT contract first');
        return;
      } else if (tenancyInfo?.is_nha_generated) {
        showErrorToast('Please delete NHA contract first');
        return;
      }
    }
    setSelected(contractType);
  };

  const isRentalOfferAmount = tenancyInfo?.open_flow_rental_offer;
  const isHoldingSelected = tenancyInfo?.open_flow_holding_deposit?.is_holding_deposit_selected;
  const hasHoldingAccount =
    !!tenancyInfo?.open_flow_holding_deposit?.OpenbrixBankAccount?.account_id &&
    !tenancyInfo?.open_flow_holding_deposit?.OpenbrixBankAccount?.is_deleted;
  const hasMoveInAccount =
    !!tenancyInfo?.open_flow_move_in?.OpenbrixBankAccount?.account_id &&
    !tenancyInfo?.open_flow_move_in?.OpenbrixBankAccount?.is_deleted;

  const is_renewed = tenancyInfo?.is_renewed;
  const message = !isRentalOfferAmount
    ? 'Please save "Rental Offer" information before generating the Tenancy Contract.'
    : is_renewed
      ? 'Please save "Renewals payments" information and "Holding Deposit" information before generating the Tenancy Contract.'
      : 'Please save "Move in payments" information and "Holding Deposit" information before generating the Tenancy Contract.';

  const checkLeadLandlordAddress = () => {
    const landlordList =
      tenancyInfo?.open_flow_landlords && tenancyInfo.open_flow_landlords.length > 0
        ? tenancyInfo.open_flow_landlords
        : [];
    const leadLandlord = landlordList.find((l) => l?.is_lead) || (landlordList.length > 0 ? landlordList[0] : null);

    if (!leadLandlord) {
      showErrorToast('Lead landlord details are required before generating Assured Periodic Tenancy (APT)');
      return false;
    }

    const leadAddress =
      leadLandlord?.correspondece_address ||
      leadLandlord?.address ||
      leadLandlord?.correspondence_address;

    if (!leadAddress || !leadAddress.toString().trim() || leadAddress === 'null') {
      showErrorToast('Lead landlord address is required before generating Assured Periodic Tenancy (APT)');
      return false;
    }

    return true;
  };

  const generateContract1 = (contract_type) => {
    if (contract_type === 'ast') {
      if (!checkLeadLandlordAddress()) {
        return;
      }
    }

    const canGenerate =
      (isHoldingSelected && hasHoldingAccount && hasMoveInAccount && isRentalOfferAmount) ||
      (!isHoldingSelected && hasMoveInAccount && isRentalOfferAmount);

    if (!canGenerate) {
      setShowAccountInfo(true);
      return;
    }

    const payload = {
      flow_id: flowId,
      contract_type,
      gurantor_deed: guarantorChecked,
    };
    dispatch(tenantActions.getAST(payload, flowId));
  };

  const deleteContract = () => {
    if (tenancyInfo?.is_ast_generated) {
      dispatch(tenantActions.removeAST(flowId));
      setShowDeleteModal(false);
    } else if (tenancyInfo?.is_nha_generated) {
      dispatch(tenantActions.deleteNHA(flowId));
      setShowDeleteModal(false);
    } else if (tenancyInfo?.bespoke_contract_url) {
      if (selectedBespokeContract && selectedBespokeContract !== 'null') {
        dispatch(tenantActions.deleteBespokeContract(flowId, selectedBespokeContract));
      }
      setShowDeleteModal(false);
      setSelectedBespokeContract('');
    }
  };

  const onDropBespokeImages = (data) => {
    if (data && data.length) {
      const newImages = data.map((file) => {
        Object.assign(file, { preview: URL.createObjectURL(file) });
        return file;
      });
      dispatch(
        tenantActions.uploadFiles(newImages, 'certificate-doc', (uploadResponse) => {
          if (uploadResponse?.data && uploadResponse.data.length) {
            const rawItem = uploadResponse.data[0];
            const fileUrl =
              typeof rawItem === 'string'
                ? rawItem
                : rawItem?.openbrixName || rawItem?.name || rawItem?.url;

            if (!fileUrl) {
              showErrorToast('Failed to get uploaded file URL');
              return;
            }

            const validExisting = Array.isArray(parsedBespokeContracts)
              ? parsedBespokeContracts.filter((c) => {
                  const u = Array.isArray(c) ? c[0] : c;
                  return u && u !== 'null';
                })
              : [];

            const currentBespoke = [...validExisting, [fileUrl]];
            setParsedBespokeContracts(currentBespoke);
            const payload = {
              bespoke_contract_url: JSON.stringify(currentBespoke),
            };
            dispatch(tenantActions.uploadManualAST(payload, flowId));
          }
        })
      );
    }
  };

  const onDropSupportingImages = (data) => {
    if (!data || !data.length) return;
    const newImages = data.map((file) => {
      Object.assign(file, { preview: URL.createObjectURL(file) });
      return file;
    });

    dispatch(
      tenantActions.uploadFiles(newImages, 'supporting-doc', (uploadResponse) => {
        const uploadedUrls = uploadResponse?.data || [];
        const newUrls = uploadedUrls
          .map((item) => (typeof item === 'string' ? item : item?.openbrixName || item?.name))
          .filter(Boolean);

        const finalDocs = [...docs, ...newUrls];
        setDocs(finalDocs);

        const payload = {
          flow_id: flowId,
          supporting_docs_url: finalDocs,
        };
        dispatch(tenantActions.uploadSupportingDocUrls(payload));
      })
    );
  };

  const deleteSupportingDocItem = (doc, index) => {
    const payload = {
      flow_id: flowId,
      deleteSupportingDoc: doc,
    };
    dispatch(tenantActions.deleteSupportingDoc(payload));
    setDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const requestESignature1 = () => {
    if (tenancyInfo?.is_ast_signed) {
      return;
    }

    if (tenancyInfo?.bespoke_contract_url) {
      setShowWarningModal(true);
    } else if (tenancyInfo?.is_ast_generated || tenancyInfo?.is_nha_generated) {
      setShowEsignatureModal(true);
    }
  };

  const handleTenantSelect = (event) => {
    const name = event.target.name;
    if (event.target.checked) {
      setSelectedTenants((prev) => [...prev, name]);
    } else {
      setSelectedTenants((prev) => prev.filter((id) => id !== name));
    }
  };

  const handleLandlordSelect = (event) => {
    const name = event.target.name;
    if (event.target.checked) {
      setSelectedLandlords((prev) => [...prev, name]);
    } else {
      setSelectedLandlords((prev) => prev.filter((id) => id !== name));
    }
  };

  const handleGuarantorSelect = (event) => {
    const name = event.target.name;
    if (event.target.checked) {
      setSelectedGuarantors((prev) => [...prev, name]);
    } else {
      setSelectedGuarantors((prev) => prev.filter((id) => id !== name));
    }
  };

  const sendEsignatureRequest1 = () => {
    const tenants = (tenancyInfo?.tenants || []).filter((tenant) =>
      selectedTenants.includes(String(tenant.open_flow_sub_tenant_id))
    );
    const landlords = (tenancyInfo?.open_flow_landlords || []).filter((landlord) =>
      selectedLandlords.includes(String(landlord.open_flow_landlord_id))
    );
    const guarantors = (tenancyInfo?.open_flow_guarantors || []).filter((guarantor) =>
      selectedGuarantors.includes(String(guarantor.open_flow_guarantor_id))
    );

    if (selectedTenants.length === 0 && selectedLandlords.length === 0 && selectedGuarantors.length === 0) {
      showErrorToast('Please select at least one Signatories');
    } else {
      dispatch(
        tenantActions.sendContractDigitally(
          { tenants, landlords, guarantors },
          flowId,
          () => {
            setShowEsignatureModal(false);
          }
        )
      );
    }
  };

  const handleEmailUrlOpen = (email, token, role) => {
    setSignatureRole(role);
    setSignatureEmail(email);
    setSignatureToken(token);
    setSendUrlOpen(true);
  };

  const onSendEmail = (payload) => {
    payload.role = signatureRole;
    payload.signatureToken = signatureToken;
    payload.signatureEmail = signatureEmail;
    TenantService.sendEmail({ flow_id: flowId, ...payload })
      .then((res) => {
        showSuccesToast('Email sent successfully');
        setSendUrlOpen(false);
      })
      .catch(() => {
        showErrorToast('Failed to send email');
        setSendUrlOpen(false);
      });
  };

  const downloadSignedAgreement1 = () => {
    dispatch(tenantActions.downloadSignedContract(flowId));
  };

  const hasGuarantor = hasAnyGuarantor(tenancyInfo?.tenants);
  const canIncludeGuarantorDeed = hasGuarantor && selected && selected !== 'bespoke';

  const hasValidBespoke =
    Array.isArray(parsedBespokeContracts) &&
    parsedBespokeContracts.some((c) => {
      const u = Array.isArray(c) ? c[0] : c;
      return u && u !== 'null';
    });

  const hasSupportingDocs =
    !!tenancyInfo?.ast_contract_url ||
    !!tenancyInfo?.nha_contract_url ||
    hasValidBespoke;

  const isImage = (url) => /\.(jpg|jpeg|png|webp)$/i.test(url);

  const renderGurantorDeedDocument = () => {
    if (!tenancyInfo?.guarantor_deed_documents_url) return null;
    return (
      <Grid item xs={12} sm={6}>
        <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
          <CardHeader
            title={
              <Typography variant="body2" style={{ fontWeight: 600, color: '#475569' }}>
                GUARANTOR DEED DOCUMENTS
              </Typography>
            }
            action={
              <Tooltip title="View Agreement">
                <IconButton
                  onClick={() => window.open(tenancyInfo.guarantor_deed_documents_url, '_blank')}
                  size="small"
                  style={{ color: '#0288D1' }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        </Card>
      </Grid>
    );
  };

  const renderASTContract = () => {
    return (
      <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0', height: '100%' }}>
        <img
          src={getImgSrc(assuredPeriodicImg)}
          alt="Assured Periodic Tenancy"
          style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
        />
        <CardContent style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#265D8C', fontSize: '1.25rem' }}>
              Assured Periodic Tenancy
            </Typography>
            <div>
              {!tenancyInfo?.ast_contract_url ? (
                <Button
                  onClick={() => generateContract1('ast')}
                  variant="contained"
                  style={{
                    background: 'linear-gradient(90deg, #A679D2 0%, #8BA9E4 100%)',
                    borderRadius: '8px',
                    padding: '8px 24px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 10px rgba(166, 121, 210, 0.3)',
                  }}
                >
                  Generate
                </Button>
              ) : (
                <IconButton color="secondary" onClick={() => setShowDeleteModal(true)} size="small">
                  <DeleteOutlineIcon fontSize="small" style={{ color: '#DC2626' }} />
                </IconButton>
              )}
            </div>
          </div>

          {tenancyInfo?.ast_contract_url && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography variant="body2" style={{ color: '#2563EB', fontSize: '13px' }}>
                      Last update on: {formatDate(tenancyInfo?.ast_generate_at)}
                    </Typography>
                  </div>
                </Grid>

                {tenancyInfo?.is_contract_deprecated && (
                  <Grid item xs={12}>
                    <AlertBox severity="warning">
                      Contract generated for this flow is deprecated.
                    </AlertBox>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                    <CardHeader
                      title={
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#475569' }}>
                          APT
                        </Typography>
                      }
                      action={
                        <Tooltip title="View Agreement">
                          <IconButton
                            onClick={() => window.open(tenancyInfo?.ast_contract_url, '_blank')}
                            size="small"
                            style={{ color: '#0288D1' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                    <CardHeader
                      title={
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#475569' }}>
                          PRESCRIBED
                        </Typography>
                      }
                      action={
                        <Tooltip title="View Agreement">
                          <IconButton
                            onClick={() => window.open(tenancyInfo?.prescribed_contract_url, '_blank')}
                            size="small"
                            style={{ color: '#0288D1' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                    <CardHeader
                      title={
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#475569' }}>
                          SUPPORTING DOCUMENTS
                        </Typography>
                      }
                      action={
                        <Tooltip title="View Agreement">
                          <IconButton
                            onClick={() => window.open(tenancyInfo?.supporting_documents_url, '_blank')}
                            size="small"
                            style={{ color: '#0288D1' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </Card>
                </Grid>

                {renderGurantorDeedDocument()}
              </Grid>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderNHAContract = () => {
    return (
      <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0', height: '100%' }}>
        <img
          src={getImgSrc(nhaAgreementImg)}
          alt="Non-Housing Act Agreement"
          style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
        />
        <CardContent style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#265D8C', fontSize: '1.25rem' }}>
              Non-Housing Act Agreement
            </Typography>
            <div>
              {!tenancyInfo?.nha_contract_url ? (
                <Button
                  onClick={() => generateContract1('nha')}
                  variant="contained"
                  style={{
                    background: 'linear-gradient(90deg, #A679D2 0%, #8BA9E4 100%)',
                    borderRadius: '8px',
                    padding: '8px 24px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 10px rgba(166, 121, 210, 0.3)',
                  }}
                >
                  Generate
                </Button>
              ) : (
                <IconButton color="secondary" onClick={() => setShowDeleteModal(true)} size="small">
                  <DeleteOutlineIcon fontSize="small" style={{ color: '#DC2626' }} />
                </IconButton>
              )}
            </div>
          </div>

          {tenancyInfo?.nha_contract_url && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography variant="body2" style={{ color: '#2563EB', fontSize: '13px' }}>
                      Last update on: {formatDate(tenancyInfo?.nha_generate_at)}
                    </Typography>
                  </div>
                </Grid>

                {tenancyInfo?.is_contract_deprecated && (
                  <Grid item xs={12}>
                    <AlertBox severity="warning">
                      Contract generated for this flow is deprecated.
                    </AlertBox>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                    <CardHeader
                      title={
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#475569' }}>
                          NHA
                        </Typography>
                      }
                      action={
                        <Tooltip title="View Agreement">
                          <IconButton
                            onClick={() => window.open(tenancyInfo?.nha_contract_url, '_blank')}
                            size="small"
                            style={{ color: '#0288D1' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                    <CardHeader
                      title={
                        <Typography variant="body2" style={{ fontWeight: 600, color: '#475569' }}>
                          SUPPORTING DOCUMENTS
                        </Typography>
                      }
                      action={
                        <Tooltip title="View Agreement">
                          <IconButton
                            onClick={() => window.open(tenancyInfo?.supporting_documents_url, '_blank')}
                            size="small"
                            style={{ color: '#0288D1' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </Card>
                </Grid>
              </Grid>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderBespokeContract = () => {
    const hasAnyValid =
      Array.isArray(parsedBespokeContracts) &&
      parsedBespokeContracts.some((c) => {
        const u = Array.isArray(c) ? c[0] : c;
        return u && u !== 'null';
      });

    const isBespokeCorrupted =
      tenancyInfo?.bespoke_contract_url && !hasAnyValid;

    return (
      <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0', height: '100%' }}>
        <CardContent style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#265D8C', fontSize: '1.25rem' }}>
              Bespoke
            </Typography>
            {tenancyInfo?.bespoke_contract_url && (
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedBespokeContract('');
                  setShowDeleteModal(true);
                }}
                style={{ color: '#DC2626' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          {isBespokeCorrupted && (
            <div
              style={{
                marginTop: 16,
                padding: '12px 16px',
                backgroundColor: '#FEF2F2',
                borderRadius: 8,
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2" style={{ color: '#991B1B', fontWeight: 500 }}>
                Invalid contract data detected.
              </Typography>
              <Button
                size="small"
                variant="contained"
                style={{ backgroundColor: '#DC2626', color: '#FFFFFF', textTransform: 'none', fontWeight: 600 }}
                onClick={() => {
                  dispatch(tenantActions.uploadManualAST({ bespoke_contract_url: null }, flowId));
                }}
              >
                Clear
              </Button>
            </div>
          )}

          {(!parsedBespokeContracts || parsedBespokeContracts.length === 0 || !hasAnyValid) && (
            <div style={{ marginTop: '20px' }}>
              <Dropzone onDrop={(acceptedFiles) => onDropBespokeImages(acceptedFiles)}>
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <div
                    {...getRootProps()}
                    style={{
                      border: '2px dashed #9CC2FF',
                      borderRadius: '16px',
                      padding: '40px 20px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: '0.25s',
                      backgroundColor: isDragActive ? '#EAF4FF' : '#F8FBFF',
                    }}
                  >
                    <input {...getInputProps()} />
                    <CloudUploadIcon style={{ fontSize: 54, color: '#1976D2' }} />
                    <Typography style={{ marginTop: '8px', fontSize: '16px', fontWeight: 600 }}>
                      Drag & drop your files here
                    </Typography>
                    <Typography style={{ opacity: 0.7, fontSize: '14px' }}>
                      or click to browse
                    </Typography>
                  </div>
                )}
              </Dropzone>
            </div>
          )}

          <Grid container spacing={2} style={{ marginTop: '16px' }}>
            {parsedBespokeContracts?.map((bespokeContract, index) => {
              const docUrl = Array.isArray(bespokeContract) ? bespokeContract[0] : bespokeContract;
              const isInvalid = !docUrl || docUrl === 'null';

              return (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    variant="outlined"
                    style={{
                      borderRadius: 8,
                      borderColor: isInvalid ? '#FECACA' : '#E2E8F0',
                      backgroundColor: isInvalid ? '#FFF5F5' : '#FFFFFF',
                    }}
                  >
                    <CardHeader
                      title={
                        <Typography
                          variant="body2"
                          style={{ fontWeight: 600, color: isInvalid ? '#DC2626' : '#475569' }}
                        >
                          {isInvalid ? `Bespoke ${index + 1} (Invalid/Null)` : `Bespoke ${index + 1}`}
                        </Typography>
                      }
                      action={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {!isInvalid && (
                            <Tooltip title="View Agreement">
                              <IconButton
                                onClick={() => window.open(docUrl, '_blank')}
                                size="small"
                                style={{ color: '#0288D1' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Agreement">
                            <IconButton
                              onClick={() => {
                                setSelectedBespokeContract(docUrl || '');
                                setShowDeleteModal(true);
                              }}
                              size="small"
                              style={{ color: '#DC2626' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      }
                    />
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderContract = () => {
    if (selected === 'ast') {
      return renderASTContract();
    } else if (selected === 'nha') {
      return renderNHAContract();
    } else if (selected === 'bespoke') {
      return renderBespokeContract();
    }
    return renderASTContract();
  };

  const SupportingDocs = () => {
    return (
      <Card variant="outlined" style={{ padding: '20px', borderRadius: 8, borderColor: '#E2E8F0', height: '100%' }}>
        <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18, marginBottom: 8, color: '#1E293B' }}>
          Supporting Documents
        </Typography>
        <Typography variant="body2" style={{ color: '#64748B', marginBottom: 16 }}>
          Please attach or upload the supporting documents.
        </Typography>
        <Dropzone onDrop={(acceptedFiles) => onDropSupportingImages(acceptedFiles)}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              style={{
                border: '2px dashed #9CC2FF',
                borderRadius: '16px',
                padding: '36px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: '0.25s',
                backgroundColor: isDragActive ? '#EAF4FF' : '#F8FBFF',
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon style={{ fontSize: 50, color: '#1976D2' }} />
              <Typography style={{ marginTop: '8px', fontSize: '15px', fontWeight: 600 }}>
                Drag & drop your files here
              </Typography>
              <Typography style={{ opacity: 0.7, fontSize: '13px' }}>
                or click to browse
              </Typography>
            </div>
          )}
        </Dropzone>

        {docs?.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0', opacity: 0.6 }}>
            <DescriptionOutlinedIcon style={{ fontSize: 44, color: '#94A3B8' }} />
            <Typography style={{ marginTop: 8, fontSize: '14px', color: '#64748B' }}>
              No supporting documents uploaded
            </Typography>
          </div>
        )}

        <Grid container spacing={2} style={{ marginTop: 16 }}>
          {docs?.map((doc, index) => (
            <Grid item xs={6} sm={4} key={index}>
              <Card
                variant="outlined"
                style={{
                  position: 'relative',
                  borderRadius: 8,
                  height: 110,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: '#FAFAFA',
                  borderColor: '#E2E8F0',
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSupportingDocItem(doc, index);
                  }}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    zIndex: 3,
                    padding: 4,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>

                {isImage(doc) ? (
                  <img
                    src={doc}
                    alt="preview"
                    onClick={() => window.open(doc, '_blank')}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '6px',
                    }}
                  />
                ) : (
                  <div
                    onClick={() => window.open(doc, '_blank')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      height: '100%',
                      padding: 8,
                    }}
                  >
                    <DescriptionOutlinedIcon style={{ fontSize: 32, color: '#666' }} />
                    <Typography variant="caption" style={{ marginTop: 4, fontSize: '11px', color: '#666' }}>
                      Preview
                    </Typography>
                  </div>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Card>
    );
  };

  const renderSendContractOptions = () => {
    return (
      <Grid item xs={12}>
        <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
          <CardHeader
            title={
              <Typography variant="h6" style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 18 }}>
                Send Agreement
              </Typography>
            }
            action={
              <div>
                {tenancyInfo?.is_ast_signed ? (
                  <Button
                    onClick={downloadSignedAgreement1}
                    variant="contained"
                    color="primary"
                    style={{
                      borderRadius: '8px',
                      padding: '8px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Download Signed Agreement
                  </Button>
                ) : tenancyInfo?.envid ? (
                  <Button
                    onClick={() => dispatch(tenantActions.updateYotiDocSignatureStatus(flowId))}
                    variant="contained"
                    color="primary"
                    style={{
                      borderRadius: '8px',
                      padding: '8px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Re-fetch Signature Status
                  </Button>
                ) : (
                  <Button
                    onClick={requestESignature1}
                    variant="contained"
                    style={{
                      backgroundColor: '#8B5CF6',
                      color: '#FFFFFF',
                      borderRadius: '8px',
                      padding: '8px 24px',
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 10px rgba(139, 92, 246, 0.3)',
                    }}
                  >
                    REQUEST ESIGNATURE
                  </Button>
                )}
              </div>
            }
          />
          <CardContent>
            <AlertBox severity="error" style={{ marginBottom: 16 }}>
              Please note: Tenants must have their ID verified to complete eSign process
            </AlertBox>
            <Typography variant="body1" style={{ color: '#64748B', marginBottom: 16 }}>
              All parties can digitally sign their contract via a legally binging eSignature.
            </Typography>

            <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #E2E8F0', borderRadius: 8 }}>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>USER</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>SENT ON</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>SIGNED ON</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>STATUS</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>ACTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenancyInfo?.tenants?.map((tenant, index) => (
                    <TableRow key={`t-${index}`}>
                      <TableCell align="center" style={{ fontWeight: 500, color: '#1E293B' }}>
                        {tenant?.app_user?.user?.first_name} {tenant?.app_user?.user?.last_name} (Tenant)
                      </TableCell>
                      <TableCell align="center">{tenant.envtoken ? formatDate(tenant.contract_sent_at) : ''}</TableCell>
                      <TableCell align="center">{tenant.envtoken ? formatDate(tenant.ast_signed_at) : ''}</TableCell>
                      <TableCell align="center">
                        {tenant.envtoken ? (
                          <Chip
                            label={tenant.is_ast_signed ? 'SIGNED' : 'UNSIGNED'}
                            variant="outlined"
                            style={{
                              borderColor: tenant.is_ast_signed ? '#22C55E' : '#3B82F6',
                              color: tenant.is_ast_signed ? '#16A34A' : '#2563EB',
                              fontWeight: 600,
                              borderRadius: 16,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Not Sent"
                            variant="outlined"
                            style={{ borderColor: '#C084FC', color: '#9333EA', fontWeight: 500, borderRadius: 16 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {tenant.envtoken && !tenant.is_ast_signed ? (
                          <Tooltip title="Email Signature Link">
                            <IconButton
                              size="small"
                              onClick={() => handleEmailUrlOpen(tenant?.app_user?.user?.email, tenant.envtoken, 'tenant')}
                            >
                              <EmailOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}

                  {tenancyInfo?.open_flow_landlords?.map((landlord, index) => (
                    <TableRow key={`l-${index}`}>
                      <TableCell align="center" style={{ fontWeight: 500, color: '#1E293B' }}>
                        {landlord.first_name} {landlord.last_name} (Landlord)
                      </TableCell>
                      <TableCell align="center">{landlord.envtoken ? formatDate(landlord.contract_sent_at) : ''}</TableCell>
                      <TableCell align="center">{landlord.envtoken ? formatDate(landlord.ast_signed_at) : ''}</TableCell>
                      <TableCell align="center">
                        {landlord.envtoken ? (
                          <Chip
                            label={landlord.is_ast_signed ? 'SIGNED' : 'UNSIGNED'}
                            variant="outlined"
                            style={{
                              borderColor: landlord.is_ast_signed ? '#22C55E' : '#3B82F6',
                              color: landlord.is_ast_signed ? '#16A34A' : '#2563EB',
                              fontWeight: 600,
                              borderRadius: 16,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Not Sent"
                            variant="outlined"
                            style={{ borderColor: '#C084FC', color: '#9333EA', fontWeight: 500, borderRadius: 16 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {landlord.envtoken && !landlord.is_ast_signed ? (
                          <Tooltip title="Email Signature Link">
                            <IconButton
                              size="small"
                              onClick={() => handleEmailUrlOpen(landlord.email, landlord.envtoken, 'landlord')}
                            >
                              <EmailOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}

                  {tenancyInfo?.open_flow_guarantors?.map((guarantor, index) => (
                    <TableRow key={`g-${index}`}>
                      <TableCell align="center" style={{ fontWeight: 500, color: '#1E293B' }}>
                        {guarantor.first_name} {guarantor.last_name} (Guarantor)
                      </TableCell>
                      <TableCell align="center">{guarantor.envtoken ? formatDate(guarantor.contract_sent_at) : ''}</TableCell>
                      <TableCell align="center">{guarantor.envtoken ? formatDate(guarantor.ast_signed_at) : ''}</TableCell>
                      <TableCell align="center">
                        {guarantor.envtoken ? (
                          <Chip
                            label={guarantor.is_ast_signed ? 'SIGNED' : 'UNSIGNED'}
                            variant="outlined"
                            style={{
                              borderColor: guarantor.is_ast_signed ? '#22C55E' : '#3B82F6',
                              color: guarantor.is_ast_signed ? '#16A34A' : '#2563EB',
                              fontWeight: 600,
                              borderRadius: 16,
                            }}
                          />
                        ) : (
                          <Chip
                            label="Not Sent"
                            variant="outlined"
                            style={{ borderColor: '#C084FC', color: '#9333EA', fontWeight: 500, borderRadius: 16 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {guarantor.envtoken && !guarantor.is_ast_signed ? (
                          <Tooltip title="Email Signature Link">
                            <IconButton
                              size="small"
                              onClick={() => handleEmailUrlOpen(guarantor.email, guarantor.envtoken, 'guarantor')}
                            >
                              <EmailOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderTenantUploadedContracts = () => {
    return (
      <Grid item xs={12}>
        <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
          <CardHeader
            title={
              <Typography variant="h6" style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 18 }}>
                Agreements uploaded by tenant
              </Typography>
            }
          />
          <CardContent>
            <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #E2E8F0', borderRadius: 8 }}>
              <Table>
                <TableHead>
                  <TableRow style={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>TENANT</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>UPLOADED ON</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>STATUS</TableCell>
                    <TableCell align="center" style={{ fontWeight: 700, color: '#64748B', fontSize: 12 }}>VIEW</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenancyInfo?.tenants?.map((tenant, index) => {
                    const hasUploaded =
                      Array.isArray(tenant.uploaded_ast_contract_urls) &&
                      tenant.uploaded_ast_contract_urls.length > 0;
                    return (
                      <TableRow key={index}>
                        <TableCell width="35%" style={{ fontWeight: 500, color: '#1E293B' }}>
                          {tenant?.app_user?.user?.first_name} {tenant?.app_user?.user?.last_name} (Tenant)
                        </TableCell>
                        <TableCell align="center">
                          {tenant.ast_uploaded_at ? formatDate(tenant.ast_uploaded_at) : ''}
                        </TableCell>
                        <TableCell align="center">
                          {hasUploaded ? (
                            <Chip
                              label="Uploaded"
                              variant="outlined"
                              style={{ borderColor: '#22C55E', color: '#16A34A', fontWeight: 600, borderRadius: 16 }}
                            />
                          ) : (
                            <Chip
                              label="Not Uploaded"
                              variant="outlined"
                              style={{ borderColor: '#C084FC', color: '#9333EA', fontWeight: 500, borderRadius: 16 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {hasUploaded ? (
                            <Tooltip title="View Agreement">
                              <IconButton
                                onClick={() => {
                                  setSelectedDocs(tenant.uploaded_ast_contract_urls);
                                  setOpenDocsModal(true);
                                }}
                                size="small"
                                style={{ color: '#0288D1' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const filteredData = radioData.filter((item) => {
    const draftAgreementType = tenancyInfo?.draft_agreement_type?.toLowerCase();
    if (draftAgreementType === 'ast' && item.value === 'nha') return false;
    if (draftAgreementType === 'nha' && item.value === 'ast') return false;
    return true;
  });

  return (
    <Container>
      <CardWrapper style={{ width: '100%', marginLeft: 0, padding: 0 }}>
        <ProgressCircle props={props} />

        <Card variant="outlined" style={{ padding: '24px', borderRadius: 12, borderColor: '#E2E8F0', marginTop: 16 }}>
          <CardHeader
            style={{ padding: 0 }}
            title={
              <Typography variant="h5" style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 22 }}>
                Tenancy Information & Agreements
              </Typography>
            }
            action={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FormControlLabel
                  label="Mark As Complete"
                  disabled={tenancyInfo?.is_completed}
                  control={
                    <Checkbox
                      checked={tenancyInfo?.landlord_status === 1}
                      onChange={handleMarkAsComplete}
                      name="landlord_status"
                      color="primary"
                    />
                  }
                />
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={onRefresh} style={{ color: '#64748B' }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            }
            subheader={
              <div style={{ marginTop: 8 }}>
                <Typography variant="body1" style={{ color: '#475569', fontSize: 15 }}>
                  Digitally create, check or amend your Tenancy Agreement, and send it to all parties.
                </Typography>
                <AlertBox severity="info" style={{ marginTop: 16 }}>
                  Fill in the information below to pre-populate the standard contracts, so they are ready to send straight to all parties.
                </AlertBox>
              </div>
            }
          />

          <CardContent style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={3}>
              {/* Accordion: Tenants, Landlords & Guarantors */}
              <Grid item xs={12}>
                <Accordion
                  expanded={expanded === 'panel1'}
                  onChange={(e, isExp) => setExpanded(isExp ? 'panel1' : '')}
                  style={{
                    boxShadow: 'none',
                    border: '1px solid #E2E8F0',
                    borderRadius: 8,
                  }}
                >
                  <AccordionSummary
                    expandIcon={expanded === 'panel1' ? <RemoveIcon style={{ color: '#2563EB' }} /> : <AddIcon style={{ color: '#2563EB' }} />}
                    style={{ minHeight: 48 }}
                  >
                    <Typography variant="body1" style={{ color: '#2563EB', fontWeight: 600, fontSize: 16 }}>
                      Tenants, Landlords & Guarantors
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails style={{ padding: 16 }}>
                    <FlowStakeHolders openFlow={tenancyInfo} />
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* The Renters’ Rights Act Information Sheet 2026 */}
              <Grid item xs={12}>
                <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 18 }}>
                        The Renters’ Rights Act Information Sheet 2026
                      </Typography>
                    }
                    subheader={
                      <div style={{ marginTop: 6 }}>
                        <Typography variant="body2" style={{ color: '#64748B', fontSize: 14 }}>
                          The Information Sheet is a document produced by the government for tenants. It explains how their tenancy may be affected by the changes introduced by the Renters’ Rights Act 2025.
                        </Typography>
                        <AlertBox severity="info" style={{ marginTop: 12 }}>
                          Please note. This sheet will be automatically sent with your tenancy agreement
                        </AlertBox>
                      </div>
                    }
                  />
                  <CardContent>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid item xs={12} sm={6}>
                        <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                          <CardHeader
                            title={
                              <Typography variant="button" style={{ fontWeight: 700, color: '#1E293B', fontSize: 14 }}>
                                THE RENTERS RIGHTS ACT 2026
                              </Typography>
                            }
                            action={
                              <Tooltip title="View Document">
                                <IconButton
                                  onClick={() => window.open('https://assets.openbrix.co.uk/userdoc-4ofHm-1775844247656-0.pdf', '_blank')}
                                  size="small"
                                  style={{ color: '#0288D1' }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            }
                          />
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tenancy Agreement Document */}
              <Grid item xs={12}>
                <Card variant="outlined" style={{ borderRadius: 8, borderColor: '#E2E8F0' }}>
                  <CardHeader
                    title={
                      <Typography variant="h6" style={{ fontWeight: 700, color: '#1E3A8A', fontSize: 18 }}>
                        Tenancy Agreement Document
                      </Typography>
                    }
                  />
                  <CardContent>
                    {/* Radio Options */}
                    <Grid container spacing={3}>
                      {filteredData.map((opt) => {
                        const isSelected = selected === opt.value;
                        return (
                          <Grid item xs={12} sm={filteredData.length === 3 ? 4 : 6} key={opt.value}>
                            <div
                              onClick={() => handleChange1(opt.value)}
                              style={{
                                border: isSelected ? '1.5px solid #2563EB' : '1px solid #E2E8F0',
                                borderRadius: 8,
                                padding: 16,
                                cursor: 'pointer',
                                backgroundColor: isSelected ? '#F8FAFC' : '#FFFFFF',
                                transition: 'all 0.2s',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Radio
                                  checked={isSelected}
                                  color="primary"
                                  size="small"
                                  style={{ padding: '0 8px 0 0', color: isSelected ? '#2563EB' : undefined }}
                                />
                                <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#1E293B' }}>
                                  {opt.title}
                                </Typography>
                              </div>
                              <Typography variant="body2" style={{ color: '#64748B', marginTop: 8, paddingLeft: 28 }}>
                                {opt.content}
                              </Typography>
                            </div>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Guarantor Deed Section */}
                    <div
                      style={{
                        border: '1px solid',
                        borderColor: canIncludeGuarantorDeed ? '#E2E8F0' : '#F1F5F9',
                        borderRadius: 8,
                        padding: '16px 20px',
                        marginTop: 24,
                        backgroundColor: canIncludeGuarantorDeed ? '#F8FAFC' : '#F8FAFC',
                        opacity: canIncludeGuarantorDeed ? 1 : 0.65,
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1E293B', marginBottom: 6 }}>
                        Guarantor Deed
                      </Typography>
                      <Typography variant="body2" style={{ color: '#64748B', marginBottom: 12 }}>
                        {canIncludeGuarantorDeed
                          ? 'Guarantor(s) deed can only be included if there are guarantor(s) on this tenancy. If you select this, all guarantors will need to sign this deed.'
                          : !selected
                            ? 'Please select a tenancy type to check if a guarantor deed can be included.'
                            : selected === 'bespoke'
                              ? 'You cannot request a guarantor deed with a bespoke tenancy type.'
                              : 'You cannot request a guarantor deed as there are no guarantors on this tenancy.'}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={guarantorChecked}
                            onChange={(e) => setGuarantorChecked(e.target.checked)}
                            color="primary"
                            disabled={!canIncludeGuarantorDeed}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2" style={{ fontWeight: 500, color: '#1E293B' }}>Include Guarantor Deed</Typography>}
                      />
                      {!canIncludeGuarantorDeed && (
                        <Typography variant="caption" style={{ color: '#94A3B8', display: 'block', marginTop: 4 }}>
                          {!selected
                            ? 'Select a tenancy type to enable guarantor deed.'
                            : selected === 'bespoke'
                              ? 'Guarantor deed is not available for bespoke tenancy type.'
                              : 'Requires at least one guarantor to be added.'}
                        </Typography>
                      )}
                    </div>

                    {/* Contract Card & Supporting Docs */}
                    <Grid container spacing={3} style={{ marginTop: 24 }}>
                      {!hasSupportingDocs && <Grid item xs={12} sm={3} />}
                      <Grid item xs={12} sm={hasSupportingDocs ? 6 : 6}>
                        {renderContract()}
                      </Grid>
                      {!hasSupportingDocs && <Grid item xs={12} sm={3} />}
                      {hasSupportingDocs && (
                        <Grid item xs={12} sm={6}>
                          <SupportingDocs />
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Send Agreement (when contract exists) */}
              {(tenancyInfo?.is_ast_generated || tenancyInfo?.is_nha_generated || tenancyInfo?.bespoke_contract_url) &&
                renderSendContractOptions()}

              {/* Agreements uploaded by tenant (when contract exists) */}
              {(tenancyInfo?.is_ast_generated || tenancyInfo?.is_nha_generated || tenancyInfo?.bespoke_contract_url) &&
                renderTenantUploadedContracts()}
            </Grid>
          </CardContent>
        </Card>
      </CardWrapper>

      {/* Drawer for sending email signature URL */}
      <SendEsignatureUrlDrawer
        open={sendUrlOpen}
        signatureEmail={signatureEmail}
        toggle={() => setSendUrlOpen(false)}
        onSendEmail={onSendEmail}
      />

      {/* Delete Contract Confirmation Dialog */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure, You want to remove this contract?.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)} variant="outlined" color="default">
            No
          </Button>
          <Button onClick={deleteContract} variant="contained" style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Modal for Bespoke Esignature */}
      <Dialog open={showWarningModal} onClose={() => setShowWarningModal(false)}>
        <DialogTitle>Esignature is not available for Bespoke Contract</DialogTitle>
        <DialogActions>
          <Button onClick={() => setShowWarningModal(false)} variant="contained" color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* Esignature Signatories Modal */}
      <Dialog maxWidth="lg" fullWidth open={showEsignatureModal} onClose={() => setShowEsignatureModal(false)}>
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 600 }}>Agreement Signitories</Typography>
          <AlertBox severity="info" style={{ marginTop: 12 }}>
            Below are a list of people who are stakeholders in this agreement, Please select the people who are required to sign this agreement
          </AlertBox>
        </DialogTitle>
        <DialogContent dividers>
          <FlowStakeHolders
            handleGuarantorSelect={handleGuarantorSelect}
            handleLandlordSelect={handleLandlordSelect}
            handleTenantSelect={handleTenantSelect}
            openFlow={tenancyInfo}
            showSelect={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEsignatureModal(false)} variant="outlined" color="default">
            Cancel
          </Button>
          <Button onClick={sendEsignatureRequest1} color="primary" variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Account Info Missing Dialog */}
      <Dialog open={showAccountInfo} onClose={() => setShowAccountInfo(false)}>
        <DialogTitle>{message}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This Information is used to generate the Tenancy contract.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccountInfo(false)} variant="outlined" color="default">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Modal for tenant uploaded documents */}
      <Dialog open={openDocsModal} onClose={() => setOpenDocsModal(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ fontSize: 20, fontWeight: 600 }}>Uploaded Documents</DialogTitle>
        <DialogContent dividers style={{ padding: 24 }}>
          {(!selectedDocs || selectedDocs.length === 0) && (
            <div style={{ textAlign: 'center', padding: '48px 0', opacity: 0.7 }}>
              <DescriptionOutlinedIcon style={{ fontSize: 60, color: '#94A3B8' }} />
              <Typography variant="h6" style={{ marginTop: 16, fontWeight: 500 }}>
                No documents uploaded
              </Typography>
              <Typography variant="body2" style={{ marginTop: 8, color: '#64748B' }}>
                Tenant hasn't added any files yet.
              </Typography>
            </div>
          )}
          {selectedDocs && selectedDocs.length > 0 && (
            <Grid container spacing={2}>
              {selectedDocs.map((url, idx) => {
                const ext = url.split('.').pop()?.toLowerCase();
                const isImg = ['jpg', 'jpeg', 'png', 'webp'].includes(ext || '');
                const isPdf = ext === 'pdf';

                return (
                  <Grid item xs={6} sm={4} md={3} key={idx}>
                    <Card
                      variant="outlined"
                      onClick={() => window.open(url, '_blank')}
                      style={{
                        cursor: 'pointer',
                        height: 180,
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 8,
                        transition: '0.2s',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      }}
                    >
                      {isImg ? (
                        <img
                          src={url}
                          alt="doc"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 6,
                          }}
                          loading="lazy"
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: 8 }}>
                          {isPdf ? (
                            <PictureAsPdfOutlinedIcon style={{ fontSize: 60, color: '#D32F2F' }} />
                          ) : (
                            <DescriptionOutlinedIcon style={{ fontSize: 60, color: '#64748B' }} />
                          )}
                          <Typography variant="body2" style={{ marginTop: 8, opacity: 0.7 }}>
                            {ext?.toUpperCase()} File
                          </Typography>
                        </div>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDocsModal(false)} style={{ fontWeight: 600 }}>
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default drawerHoc({ Component: TenancyInfoAndAgreement });
